import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { CategoryPicker } from '@/components/ui/CategoryPicker';
import { EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { distanceKm } from '@/lib/geo';
import {
  CATEGORY_LABELS,
  SERVICE_CATEGORIES,
  formatZAR,
  sameCampusRegion,
  type RequestDoc,
  type ServiceCategory,
  type ServiceDoc,
} from '@makhe/shared';

type Sort = 'rating' | 'distance' | 'newest';
const SORT_LABELS: Record<Sort, string> = {
  rating: 'Top rated',
  distance: 'Nearest',
  newest: 'Newest',
};

function catsFromParams(params: URLSearchParams): ServiceCategory[] {
  const cat = params.get('cat');
  if (cat && (SERVICE_CATEGORIES as readonly string[]).includes(cat)) {
    return [cat as ServiceCategory];
  }
  return [];
}

export default function DiscoverPage() {
  const { userDoc } = useAuth();
  const [params] = useSearchParams();
  const [cats, setCats] = useState<ServiceCategory[]>(() => catsFromParams(params));
  const [sort, setSort] = useState<Sort>('rating');
  const [search, setSearch] = useState(() => params.get('q') ?? '');

  // Keep the server query simple — just active services (optionally narrowed to
  // a single category). No `orderBy` here on purpose: ordering by a field like
  // stats.averageRating silently drops any service that lacks that field, which
  // made the default (no-category) view look empty. Sorting happens in-memory
  // below, so every active service always loads.
  const q = useMemo(() => {
    const col = collection(db, 'services');
    if (cats.length === 1) {
      return query(
        col,
        where('status', '==', 'active'),
        where('category', '==', cats[0]),
        limit(50),
      );
    }
    return query(col, where('status', '==', 'active'), limit(50));
  }, [cats]);

  const { data: services, loading, error } = useCollection<ServiceDoc>(q);

  // Seller view: surface open buyer requests that match their categories + campus
  // right here in Discover (not just buried in Studio → Requests).
  const isSeller = !!userDoc?.roles?.seller;
  const sellerCats = (userDoc?.sellerProfile?.categories ?? []) as ServiceCategory[];
  const myCampus = userDoc?.location?.campus;
  const reqQ = useMemo(() => {
    if (!isSeller || sellerCats.length === 0) return null;
    return query(
      collection(db, 'requests'),
      where('status', '==', 'open'),
      where('category', 'in', sellerCats.slice(0, 10)),
      orderBy('createdAt', 'desc'),
      limit(10),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeller, sellerCats.join(',')]);
  const { data: openRequests } = useCollection<RequestDoc>(reqQ);
  // Exclude the viewer's own requests — a seller shouldn't be offered their own.
  const myRequests = openRequests.filter(
    (r) => r.buyerId !== userDoc?.uid && sameCampusRegion(r.campus, myCampus),
  );

  const filtered = useMemo(() => {
    let list = services;
    if (cats.length > 1) {
      list = list.filter((s) => cats.includes(s.category));
    }
    const s = search.trim().toLowerCase();
    if (s) {
      list = list.filter((svc) =>
        [
          svc.title,
          svc.description,
          svc.sellerSnapshot.displayName,
          CATEGORY_LABELS[svc.category],
          svc.category,
          svc.subcategory ?? '',
        ]
          .join(' ')
          .toLowerCase()
          .includes(s),
      );
    }
    if (sort === 'distance' && userDoc?.location?.geoPoint) {
      const me = userDoc.location.geoPoint;
      return [...list].sort(
        (a, b) =>
          distanceKm(me, a.serviceArea.geoPoint) - distanceKm(me, b.serviceArea.geoPoint),
      );
    }
    if (sort === 'newest') {
      return [...list].sort(
        (a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0),
      );
    }
    // 'rating' (default) — sort in-memory so services without reviews yet
    // (averageRating missing/0) still appear, just lower down.
    return [...list].sort(
      (a, b) => (b.stats?.averageRating ?? 0) - (a.stats?.averageRating ?? 0),
    );
  }, [services, cats, search, sort, userDoc]);

  return (
    <section className="mx-auto max-w-6xl px-6 py-10">
      {/* — Editorial header — */}
      <div className="flex items-baseline justify-between border-b border-makhe-line pb-3">
        <p className="eyebrow">Discover</p>
        <p className="font-display text-sm italic text-makhe-muted">
          {loading ? '…' : `${filtered.length} creators`}
        </p>
      </div>
      <h1
        className="mt-6 font-display text-5xl leading-none md:text-7xl"
        style={{ fontVariationSettings: '"opsz" 144, "wght" 350' }}
      >
        Student beauty on campus.
      </h1>

      {/* — Search row — typewriter underline — */}
      <div className="mt-10 flex flex-col gap-6 md:flex-row md:items-end md:gap-10">
        <label className="flex-1">
          <span className="eyebrow">Search</span>
          <input
            placeholder="braids, nails, lashes, knotless…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mt-3 w-full border-0 border-b border-makhe-ink/60 bg-transparent pb-2 font-display text-2xl placeholder:text-makhe-muted/70 focus:border-makhe-ink focus:outline-none"
          />
        </label>
        <SegmentedControl
          size="sm"
          value={sort}
          onChange={setSort}
          options={(['rating', 'distance', 'newest'] as Sort[]).map((s) => ({
            value: s,
            label: SORT_LABELS[s],
          }))}
        />
      </div>

      <div className="mt-6">
        <CategoryPicker value={cats} onChange={setCats} />
      </div>

      <Link
        to="/requests/new"
        className="mt-6 flex items-center justify-between rounded-md border border-dashed border-makhe-line bg-makhe-paper px-5 py-4 transition-colors hover:border-makhe-primary"
      >
        <div>
          <p className="font-display text-lg">Can't find it? Post a request.</p>
          <p className="text-sm text-makhe-muted">Describe what you want and let creators send you offers.</p>
        </div>
        <span className="shrink-0 text-[11px] uppercase tracking-widest text-makhe-primary">Post →</span>
      </Link>

      {myRequests.length > 0 && (
        <div className="mt-10">
          <div className="flex items-baseline justify-between">
            <h2
              className="font-display text-2xl"
              style={{ fontVariationSettings: '"opsz" 36, "wght" 450' }}
            >
              Students looking for you
            </h2>
            <Link
              to="/seller/requests"
              className="text-[11px] uppercase tracking-widest text-makhe-primary"
            >
              See all →
            </Link>
          </div>
          <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
            {myRequests.map((r) => (
              <Link
                key={r.id}
                to={`/seller/requests/${r.id}`}
                className="w-60 shrink-0 rounded-md border border-makhe-line bg-makhe-paper p-4 transition-colors hover:border-makhe-primary"
              >
                <p className="text-[10px] uppercase tracking-widest text-makhe-primary">
                  {CATEGORY_LABELS[r.category]}
                </p>
                <p className="mt-1 line-clamp-2 font-medium">{r.title}</p>
                <p className="mt-1 text-xs text-makhe-muted">
                  {r.buyerSnapshot.displayName}
                  {r.campus ? ` · ${r.campus}` : ''}
                </p>
                <p className="mt-2 text-xs text-makhe-muted/80">
                  {r.budgetHintZARcents != null ? `~ ${formatZAR(r.budgetHintZARcents)}` : 'No budget'} · {r.offerCount} offer{r.offerCount === 1 ? '' : 's'}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* — Editorial grid — */}
      <div className="mt-12 grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-3">
        {loading &&
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <LoadingSkeleton className="aspect-[3/4] w-full" />
              <LoadingSkeleton className="mt-3 h-4 w-2/3" />
              <LoadingSkeleton className="mt-2 h-3 w-1/3" />
            </div>
          ))}

        {!loading && error && (
          <div className="col-span-full">
            <EmptyState
              title="Couldn’t load services."
              description="Something went wrong fetching services. Pull to refresh or try again in a moment."
            />
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              title="Nothing in this corner."
              description="Try a different category, or post a request and let creators come to you."
              action={
                <Link to="/requests/new" className="btn-primary">
                  Post a request
                </Link>
              }
            />
          </div>
        )}

        {filtered.map((svc, idx) => (
          <Link
            key={svc.id}
            to={`/service/${svc.id}`}
            className="group block animate-rise"
            style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
          >
            <div className="relative overflow-hidden rounded-md border border-makhe-line bg-makhe-night shadow-soft transition-shadow group-hover:shadow-lift">
              <div className="aspect-[3/4] w-full">
                {svc.media.coverImageURL || svc.media.videoThumbnailURL ? (
                  <img
                    src={svc.media.coverImageURL || svc.media.videoThumbnailURL}
                    alt={`${svc.title} by ${svc.sellerSnapshot.displayName}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <video
                    src={svc.media.videoURL}
                    muted
                    playsInline
                    preload="none"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
              </div>
              <div className="absolute left-3 top-3 rounded-full bg-makhe-bg/90 px-3 py-1 text-[10px] uppercase tracking-widest text-makhe-ink">
                {CATEGORY_LABELS[svc.category]}
              </div>
            </div>

            <div className="mt-4 flex items-baseline justify-between gap-3">
              <span className="font-mono text-[11px] text-makhe-muted">
                {String(idx + 1).padStart(2, '0')}
              </span>
              <span className="text-[11px] uppercase tracking-widest text-makhe-ochre">
                ★ {svc.stats.averageRating.toFixed(1)}{' '}
                <span className="text-makhe-muted">({svc.stats.totalReviews})</span>
              </span>
            </div>

            <h3
              className="mt-1 line-clamp-2 font-display text-xl leading-tight"
              style={{ fontVariationSettings: '"opsz" 36, "wght" 450' }}
            >
              {svc.title}
            </h3>
            <p className="mt-1 text-sm italic text-makhe-muted">
              {svc.sellerSnapshot.displayName}
            </p>
            <p className="mt-2 text-[11px] uppercase tracking-widest text-makhe-ink">
              From{' '}
              <span className="font-mono normal-case tracking-normal text-makhe-primary">
                {formatZAR(Math.min(...svc.pricingTiers.map((t) => t.priceZARcents)))}
              </span>
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}
